import styled, { keyframes, css } from "styled-components";
import { palette } from "../../styles/palette";

export { css };

export const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 ${palette.greenMainLight}; }
  70% { box-shadow: 0 0 0 16px ${palette.greenMainLight.replace("0.6", "0")}; }
  100% { box-shadow: 0 0 0 0 ${palette.greenMainLight}; }
`;

export const blink = keyframes`
  0% { opacity: 1; }
  12.5% { opacity: 0.3; }
  25% { opacity: 1; }
  37.5% { opacity: 0.3; }
  50% { opacity: 1; }
  100% { opacity: 1; }
`;

export const RefreshButton = styled.button<{
  $isBlinking: boolean;
  $isPlaying: boolean;
}>`
  align-self: center;
  background: transparent;
  border: none;
  color: ${palette.grenTitle};
  font-size: 16px;
  cursor: ${({ $isPlaying }) => ($isPlaying ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 6px;
  transition: all 0.2s ease;
  opacity: ${({ $isPlaying }) => ($isPlaying ? 0.5 : 1)};
  text-decoration: underline;
  text-decoration-color: ${palette.grenTitle};
  text-underline-offset: 2px;
  font-weight: 500;
  animation: ${({ $isBlinking }) =>
    $isBlinking
      ? css`
          ${blink} 2s ease-in-out
        `
      : "none"};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const PreparationScreenContainer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: ${palette.white};
  display: flex;
  min-height: 100vh;
  overflow-y: auto;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      ${palette.greenSecond} 0%,
      ${palette.greenMainLight} 100%
    );
    opacity: 0.1;
    z-index: -1;
  }
`;

export const PreparationContent = styled.div`
  display: flex;
  flex: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  gap: 80px;
  align-items: center;
  justify-content: center;
  min-height: 100vh;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 40px;
    padding: 20px 16px;
    min-height: auto;
  }

  @media (max-width: 480px) {
    gap: 30px;
    padding: 16px 12px;
  }
`;

export const AudioSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 60px 30px 0 30px;
  border-radius: 16px;
`;

export const AudioCheckTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${palette.grenTitle};
  margin: 0 0 16px 0;
  text-align: center;
  flex-shrink: 0;

  @media (max-width: 480px) {
    font-size: 18px;
    margin: 0 0 8px 0;
  }
`;

export const AudioCheckSubtitle = styled.p`
  font-size: 16px;
  color: ${palette.secondaryTextColor};
  margin: 0 0 24px 0;
  text-align: center;
  line-height: 1.5;
  flex-shrink: 0;

  @media (max-width: 480px) {
    font-size: 13px;
    margin: 0 0 16px 0;
  }
`;

export const AudioVisualizer = styled.div`
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: ${palette.greenSecond};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0 16px 0;
  position: relative;
  border: 4px solid ${palette.white};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);

  @media (max-width: 480px) {
    width: 180px;
    height: 180px;
    margin: 16px 0 24px 0;
  }
`;

export const AudioLevelIndicator = styled.div<{ level: number }>`
  width: ${({ level }) => Math.max(20, level * 0.8)}%;
  height: ${({ level }) => Math.max(20, level * 0.8)}%;
  border-radius: 50%;
  background: ${({ level }) =>
    level > 50
      ? palette.orange
      : level > 20
      ? palette.greenMain
      : palette.greyFont};
  transition: all 0.1s ease;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

export const AudioStatus = styled.div<{ hasPermission: boolean }>`
  font-size: 18px;
  font-weight: 600;
  color: ${({ hasPermission }) =>
    hasPermission ? palette.greenMain : palette.dangerColor};
  margin-bottom: 24px;
  text-align: center;
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const AudioCheckButtons = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  max-width: 400px;
  flex-wrap: wrap;
  justify-content: center;
`;

export const AudioCheckButton = styled.button<{
  variant?: "primary" | "secondary";
}>`
  background: ${({ variant }) =>
    variant === "primary" ? palette.orange : "transparent"};
  color: ${({ variant }) =>
    variant === "primary" ? palette.white : palette.secondaryTextColor};
  border: ${({ variant }) =>
    variant === "primary" ? "none" : `1px solid ${palette.greenSecond}`};
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  height: fit-content;

  &:hover {
    background: ${({ variant }) =>
      variant === "primary" ? palette.orange : palette.greenSecond};
    color: ${({ variant }) =>
      variant === "primary" ? palette.white : palette.grenTitle};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 480px) {
    padding: 10px 16px;
    font-size: 14px;
    min-width: 100px;
  }
`;

export const RequirementsSection = styled.div`
  flex: 1;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  @media (max-width: 768px) {
    padding-bottom: 40px;
  }
`;

export const PreparationTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${palette.grenTitle};
  margin: 0 0 32px 0;
  text-align: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 26px;
    margin: 0 0 20px 0;
  }

  @media (max-width: 480px) {
    font-size: 22px;
    margin: 0 0 16px 0;
  }
`;

export const StartButton = styled.button`
  background: ${palette.orange};
  color: ${palette.white};
  border: 4px solid ${palette.orange};
  border-radius: 8px;
  padding: 12px 32px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin: 48px auto 0 auto;
  transition: all 0.3s;
  box-shadow: 0 4px 12px ${palette.shadowMedium};
  display: block;
  width: fit-content;

  &:hover {
    background: ${palette.orangeTwo};
    border-color: ${palette.orangeTwo};
    transform: translateY(-2px);
    box-shadow: 0 6px 16px ${palette.shadowDark};
  }

  &:disabled {
    background: ${palette.greyFont};
    border-color: ${palette.greyFont};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-top: 40px;
    font-size: 15px;
    padding: 12px 24px;
  }

  @media (max-width: 480px) {
    margin-top: 32px;
    font-size: 14px;
    padding: 10px 20px;
  }
`;

// Pantalla de prueba de audio
export const AudioTestScreen = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: ${palette.white};
  display: flex;
  min-height: 100vh;
  overflow-y: auto;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      ${palette.greenSecond} 0%,
      ${palette.greenMainLight} 100%
    );
    opacity: 0.1;
    z-index: -1;
  }
`;

export const AudioTestContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px 20px;
  max-width: 500px;
  margin: 0 auto;
`;

export const AudioTestTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${palette.grenTitle};
  margin: 0 0 24px 0;
  text-align: center;
`;

export const AudioTestSubtitle = styled.p`
  font-size: 18px;
  color: ${palette.secondaryTextColor};
  margin: 0 0 40px 0;
  text-align: center;
  line-height: 1.5;
`;

export const AudioTestCard = styled.div`
  background: ${palette.white};
  border-radius: 8px;
  padding: 50px 40px;
  width: 100%;
  max-width: 700px;
  text-align: center;
  border: 1.5px solid ${palette.greenSecond};
`;

export const AudioTestIcon = styled.div`
  margin-bottom: 24px;
  color: ${palette.grenTitle};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const AudioTestMessage = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${palette.grenTitle};
  margin-bottom: 16px;
  line-height: 1.4;
  padding: 0 8px;
  max-width: 100%;
`;

export const AudioTestQuestion = styled.div`
  font-size: 16px;
  color: ${palette.secondaryTextColor};
  margin-bottom: 32px;
  line-height: 1.5;
`;

export const AudioTestButtons = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  max-width: 400px;
`;

export const AudioTestButton = styled.button<{
  variant: "success" | "danger" | "primary" | "secondary";
}>`
  background: ${({ variant }) =>
    variant === "success"
      ? palette.orange
      : variant === "danger"
      ? palette.white
      : variant === "secondary"
      ? palette.white
      : palette.orange};
  color: ${({ variant }) =>
    variant === "danger" || variant === "secondary"
      ? palette.greyFont
      : palette.white};
  border: ${({ variant }) =>
    variant === "danger" || variant === "secondary"
      ? `2px solid ${palette.greyFont}`
      : "none"};
  border-radius: 8px;
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  flex: 1;
  transition: all 0.3s;
  box-shadow: ${palette.boxShadowCard};

  &:hover {
    background: ${({ variant }) =>
      variant === "success"
        ? palette.orangeTwo
        : variant === "danger"
        ? palette.greyFont
        : variant === "secondary"
        ? palette.greyFont
        : palette.orangeTwo};
    color: ${({ variant }) =>
      variant === "danger" || variant === "secondary"
        ? palette.white
        : palette.white};
    transform: translateY(-1px);
  }

  &:disabled {
    background: ${({ variant }) =>
      variant === "success"
        ? palette.orange
        : variant === "danger"
        ? palette.white
        : variant === "secondary"
        ? palette.white
        : palette.orange};
    color: ${({ variant }) =>
      variant === "danger" || variant === "secondary"
        ? palette.greyFont
        : palette.white};
    cursor: not-allowed;
    opacity: 0.5;
    transform: none;
  }

  @media (max-width: 480px) {
    padding: 10px 16px;
    font-size: 14px;
  }
`;

export const RequirementIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
  color: ${palette.grenTitle};
`;

export const RequirementText = styled.p`
  font-size: 16px;
  color: ${palette.secondaryTextColor};
  margin: 0;
  line-height: 1.5;

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 1.4;
  }
`;

export const RequirementItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 18px;
  gap: 14px;
  padding: 0 8px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export const AudioVisualizerIcon = styled.div`
  font-size: 48px;
  color: ${palette.white};
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

export const AudioTestButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 400px;
`;

export const DeviceSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 400px;
`;

export const DeviceSelectorLabel = styled.label`
  font-size: 15px;
  font-weight: 600;
  color: ${palette.grenTitle};
  text-align: left;
  letter-spacing: 0.2px;
`;

export const DeviceSelector = styled.select`
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 1.5px solid ${palette.greenSecond};
  border-radius: 8px;
  background: ${palette.white};
  color: ${palette.grenTitle};
  font-size: 15px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.3s ease;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  box-shadow: 0 1px 3px ${palette.shadowLight};
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 14 14'%3E%3Cpath fill='%23003D53' d='M7 10L2 5h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;

  &:focus {
    outline: none;
    border-color: ${palette.orange};
    box-shadow: 0 0 0 3px ${palette.greenMainLight},
      0 2px 6px ${palette.shadowMedium};
    background-color: ${palette.white};
  }

  &:hover {
    border-color: ${palette.greenMain};
    background-color: ${palette.ultraPale};
    box-shadow: 0 2px 6px ${palette.shadowMedium};
  }

  option {
    padding: 12px;
    background: ${palette.white};
    color: ${palette.grenTitle};
    font-size: 15px;
  }
`;

export const SpeakerSelectorRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  max-width: 400px;
  align-items: flex-end;
  margin-bottom: 0;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const SpeakerSelectorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  width: 100%;
`;

export const SpeakerSelectorLabel = styled.label`
  font-size: 15px;
  font-weight: 600;
  color: ${palette.grenTitle};
  text-align: left;
  letter-spacing: 0.2px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const SpeakerSelectorIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${palette.grenTitle};
`;

export const SpeakerSelector = styled.select`
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 1.5px solid ${palette.greenSecond};
  border-radius: 8px;
  background: ${palette.white};
  color: ${palette.grenTitle};
  font-size: 15px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.3s ease;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  box-shadow: 0 1px 3px ${palette.shadowLight};
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 14 14'%3E%3Cpath fill='%23003D53' d='M7 10L2 5h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;

  &:focus {
    outline: none;
    border-color: ${palette.orange};
    box-shadow: 0 0 0 3px ${palette.greenMainLight},
      0 2px 6px ${palette.shadowMedium};
    background-color: ${palette.white};
  }

  &:hover {
    border-color: ${palette.greenMain};
    background-color: ${palette.ultraPale};
    box-shadow: 0 2px 6px ${palette.shadowMedium};
  }

  option {
    padding: 12px;
    background: ${palette.white};
    color: ${palette.grenTitle};
    font-size: 15px;
  }
`;
